import React, { useState } from 'react';
import { Trophy, ChevronRight, ArrowLeft } from 'lucide-react';
import { cn } from '../lib/utils';
import { trackEvent } from '../lib/analytics';
import { useSettings } from './SettingsContext';
import Footer from './Footer';

interface ConfigScreenProps {
  stats: { played: number; won: number; lost: number };
  onStartGame: (numPlayers: number, difficulty: 'easy' | 'medium' | 'hard') => void;
  onBack: () => void;
}

export default function ConfigScreen({
  stats,
  onStartGame,
  onBack,
}: ConfigScreenProps) {
  const { tableTheme } = useSettings();
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');

  const startGame = (numPlayers: number) => {
    trackEvent('game_start', {
      mode: 'cpu',
      difficulty,
      players: numPlayers,
    });
    onStartGame(numPlayers, difficulty);
  };

  return (
    <div
      className={cn(
        'fixed inset-0 game-bg text-white font-sans select-none overflow-y-auto',
        `game-bg-${tableTheme}`,
      )}
    >
      <div className="min-h-[100dvh] w-full flex flex-col items-center justify-start p-4 pb-8">
        <div
          className="w-full max-w-md flex justify-start pt-2 z-10 shrink-0"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}
        >
          <button
            onClick={onBack}
            className="p-2.5 glass-panel rounded-xl transition-all hover:scale-105 active:scale-95"
            aria-label="Torna al menu"
          >
            <ArrowLeft className="w-5 h-5 text-white/60" />
          </button>
        </div>

        <div className="w-full max-w-md my-auto py-6 text-center space-y-8 flex flex-col">
          <div className="space-y-2 sm:space-y-3">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-[0.15em] uppercase text-yellow-400" style={{ fontFamily: '"Playfair Display", serif', textShadow: '0 0 40px rgba(251,191,36,0.3)' }}>
              SBARAZZINO
            </h1>
            <p className="text-white/50 text-xs sm:text-sm uppercase tracking-[0.3em]">Scopa Speciale · Carte Napoletane</p>
          </div>

          <div className="flex justify-center">
            <div className="w-16 h-24 sm:w-20 sm:h-28 glass-panel rounded-2xl flex items-center justify-center">
              <Trophy className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-500/40" />
            </div>
          </div>

          {stats.played > 0 && (
            <div className="flex justify-center gap-6 sm:gap-8 py-4 px-6 glass-panel rounded-3xl">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-black text-yellow-400" style={{ fontFamily: '"Playfair Display", serif' }}>{stats.played}</div>
                <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">Partite</div>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-black text-green-400" style={{ fontFamily: '"Playfair Display", serif' }}>{stats.won}</div>
                <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">Vittorie</div>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-black text-red-400" style={{ fontFamily: '"Playfair Display", serif' }}>{stats.lost}</div>
                <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">Sconfitte</div>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-black text-cyan-400" style={{ fontFamily: '"Playfair Display", serif' }}>
                  {stats.played > 0 ? Math.round((stats.won / stats.played) * 100) : 0}%
                </div>
                <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">Win Rate</div>
              </div>
            </div>
          )}

          <div className="space-y-3 sm:space-y-4">
            <p className="text-white/60 text-xs sm:text-sm font-bold uppercase tracking-widest">Difficoltà CPU</p>
            <div className="flex gap-2">
              {(['easy', 'medium', 'hard'] as const).map((diff) => (
                <button
                  key={diff}
                  onClick={() => setDifficulty(diff)}
                  className={cn(
                    'flex-1 py-2.5 rounded-xl border text-xs font-bold uppercase tracking-wider transition-all',
                    difficulty === diff
                      ? 'glass-panel-amber border-yellow-500 text-yellow-400 font-extrabold shadow-[0_4px_12px_rgba(234,179,8,0.15)] scale-[1.02]'
                      : 'bg-white/5 border-white/5 text-white/60 hover:border-white/10 hover:text-white',
                  )}
                >
                  {diff === 'easy' ? 'Facile' : diff === 'medium' ? 'Medio' : 'Difficile'}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <p className="text-white/60 text-xs sm:text-sm font-bold uppercase tracking-widest">Quanti giocatori?</p>
            <div className="flex flex-col gap-2 sm:gap-3">
              <button
                onClick={() => startGame(2)}
                className="w-full py-4 px-6 glass-panel hover:glass-panel-amber rounded-2xl text-left transition-all hover:scale-[1.02] active:scale-[0.98] group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xl font-black text-white group-hover:text-yellow-400 transition-colors">2 Giocatori</div>
                    <div className="text-xs text-white/40 mt-0.5">Tu + CPU</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-yellow-500 transition-colors" />
                </div>
              </button>

              <button
                onClick={() => startGame(3)}
                className="w-full py-4 px-6 glass-panel hover:glass-panel-amber rounded-2xl text-left transition-all hover:scale-[1.02] active:scale-[0.98] group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xl font-black text-white group-hover:text-yellow-400 transition-colors">3 Giocatori</div>
                    <div className="text-xs text-white/40 mt-0.5">Tu + 2 CPU</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-yellow-500 transition-colors" />
                </div>
              </button>
            </div>
          </div>

          <Footer />
        </div>
      </div>
    </div>
  );
}
